import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {SelectButton} from "primereact/selectbutton";
import {Tooltip} from "primereact/tooltip";
import type {ReactNode, RefObject} from "react";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import {
    type DescriptionKeywordCategoriesMap,
    type DescriptionKeywordKeyNames,
    type KeywordToken,
    parseKeywordTokens,
} from "@reva/shared/description-keywords";
import {formatDistanceM} from "@reva/shared/number-format";
import {
    limitPoisPerType,
    type PoiFeatureLike,
    type PoiRankingProperties,
    type PoiVehicle,
    resolveVehicleForPoi,
} from "@reva/shared/poi";
import type {AvmSpecParamCategory} from "@reva/shared/reva-spec";

import type {NoiseItem} from "../../api/payload-types/locationinfo-noise";
import {NOISE_DB_NO_IMPACT, noiseLevelLabel} from "../../api/payload-types/locationinfo-noise";
import MapLayerUnavailableHint from "../../component/MapLayerUnavailableHint";
import OpenLayersMap from "../../component/OpenLayersMap";
import {useLanguage, useRevaTranslation} from "../../hooks/languageHooks";
import {useBasemapDataYear} from "../../hooks/useBasemapDataYear";
import {selectAddressData} from "../../redux/addressSlice";
import {selectPoisDataAggregated} from "../../redux/locationInfoSelectors";
import {selectEmissionData, selectNoiseData, setShowTrafficMap} from "../../redux/locationInfoSlice";
import {useRevaSelector} from "../../redux/revaHooks";
import {selectDarkTheme} from "../../redux/themeSlice";
import {LAYER_BASEMAP, LAYER_POIS_DECORATED, selectCapabilities} from "../../redux/wmtsSlice";
import {createScaleView, LayerFactory} from "../../util/maps";
import {POIS_VEHICLE_TO_ICON} from "../../util/pois";
import {DEFAULT_NEARBY_RADII, DEFAULT_TRAFFIC_RADII, DEFAULT_TRAFFIC_SCALE} from "../location/TabPoisLocation";
import {
    addedRanges,
    DESCRIPTION_KEYWORD_POI_TYPES,
    replaceKeywordToken,
    tooltipKindForKeyword,
} from "./DescriptionKeywordUtil";

interface FilterablePoiProperties extends PoiRankingProperties {
    name?: string | null;
    type_de?: string | null;
    poi_uid?: string;
}

interface PoiRow {
    uid: string | undefined;
    name: string | null | undefined;
    type: string | null | undefined;
    distance: number | null | undefined;
    duration: number | null | undefined;
    vehicle: PoiVehicle;
}

const POIS_PER_TYPE_IN_DESCRIPTION = 1;

/** 2 Supermärkten anzeigen, wenn vorhanden (übereinstimmend mit generierter Beschreibung) */
function rankPoisWithSupermarketMinimum<F extends PoiFeatureLike<FilterablePoiProperties>>(
    features: F[],
    vehicle: PoiVehicle,
    maxRank: number,
): F[] {
    return [
        ...limitPoisPerType(
            features.filter((feature) => feature.properties.type_en !== "Supermarket"),
            vehicle,
            maxRank,
        ),
        ...limitPoisPerType(
            features.filter((feature) => feature.properties.type_en === "Supermarket"),
            vehicle,
            Math.max(maxRank, 2),
        ),
    ];
}

function PoiTooltipTable({
    typeEnList,
    keywordName,
    isTooltip,
}: {
    typeEnList: string[];
    keywordName: string;
    isTooltip: boolean;
}) {
    const {t, language} = useRevaTranslation();
    const isDarkTheme = useSelector(selectDarkTheme);
    const poisData = useRevaSelector(selectPoisDataAggregated);
    const isIdv = keywordName === "idv";
    const vehicle: PoiVehicle = isIdv ? "car" : "foot";
    const features = (poisData?.features ?? []) as PoiFeatureLike<FilterablePoiProperties>[];

    const filteredFeatures = features.filter((feature) => typeEnList.includes(feature.properties.type_en ?? ""));
    const rankedFeatures = rankPoisWithSupermarketMinimum(filteredFeatures, vehicle, POIS_PER_TYPE_IN_DESCRIPTION);

    let rows: PoiRow[] = rankedFeatures
        .map((feature): PoiRow => {
            const properties = feature.properties;
            const resolvedVehicle = resolveVehicleForPoi(vehicle, properties);
            const type = properties[`type_${language}` as "type_de"];
            return {
                uid: properties.poi_uid,
                name: properties.name,
                // idv-Typen wie "Motorway link (single-digit)" -> Klammer-Zusatz nicht anzeigen.
                type: isIdv ? (type?.replace(/\s*\([^)]*\)/g, "") ?? type) : type,
                distance: properties[`${resolvedVehicle}_path_length_m`],
                duration: properties[`${resolvedVehicle}_duration_min`],
                vehicle: resolvedVehicle,
            };
        })
        .sort((a, b) => (a.distance ?? Number.POSITIVE_INFINITY) - (b.distance ?? Number.POSITIVE_INFINITY));

    // idv-Typen überschneiden sich für dieselbe Auffahrt -> Dubletten mit gleicher Entfernung zusammenfassen.
    if (isIdv) {
        const seenDistances = new Set<number | null | undefined>();
        rows = rows.filter((row) => {
            if (seenDistances.has(row.distance)) return false;
            seenDistances.add(row.distance);
            return true;
        });
    }

    if (rows.length === 0) {
        return <p className="m-0 text-sm">{t("market.description.tooltip.noPois")}</p>;
    }

    return (
        <table className="text-sm w-full">
            <thead>
                <tr className="text-left">
                    <th className="pr-3 pb-1">{t("pois.table_header.type")}</th>
                    <th className="pr-3 pb-1">{t("pois.table_header.name")}</th>
                    <th className="pr-3 pb-1 whitespace-nowrap">{t("pois.table_header.distance")}</th>
                    <th className="pb-1 whitespace-nowrap">{t("pois.table_header.duration")}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.uid}>
                        <td className="pr-3">{row.type}</td>
                        <td className="pr-3">{row.name}</td>
                        <td className="pr-3 whitespace-nowrap">{formatDistanceM(row.distance, language)}</td>
                        <td className="whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                                {row.duration != null ? `${row.duration} min` : "-"}
                                {/* Tooltip-Hintergrund ist immer dunkel -- Dialog-Hintergrund folgt dem Theme. */}
                                <img
                                    alt={row.vehicle}
                                    src={
                                        POIS_VEHICLE_TO_ICON[
                                            `${row.vehicle}${isTooltip || isDarkTheme ? "_white" : ""}`
                                        ]
                                    }
                                    style={{width: "16px"}}
                                />
                            </span>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

const MINI_MAP_NEARBY_SCALE = 15000;

/** Mini-Karte mit den POIs, deren Nähe die aktuelle Einstufung begründet (dieselbe Auswahl wie
 * PoiTooltipTable), als räumlicher Kontext im Änderungsdialog. */
function PoiTooltipMiniMap({typeEnList, keywordName}: {typeEnList: string[]; keywordName: string}) {
    const language = useLanguage();
    const basemapYear = useBasemapDataYear();
    const addressData = useRevaSelector(selectAddressData);
    const capabilities = useRevaSelector(selectCapabilities);
    const poisData = useRevaSelector(selectPoisDataAggregated);
    const vehicle: PoiVehicle = keywordName === "idv" ? "car" : "foot";
    const address = addressData?.address;
    const basemapCapabilities = capabilities?.[LAYER_BASEMAP];
    const osmCapabilities = capabilities?.[LAYER_POIS_DECORATED];
    const features = (poisData?.features ?? []) as PoiFeatureLike<FilterablePoiProperties>[];

    if (!address) return null;

    // #19906 ohne Basemap dient die OpenStreetMap-Karte als Hintergrund; fehlen beide, zeigt die
    // Karte nur Kreise, POIs und Adresse, und MapLayerUnavailableHint nennt den Ausfall.
    const backgroundLayer = basemapCapabilities
        ? // maps.jsx JSDoc types commonOptions as CommonLayerOptions (no `params`), but
          // createGeoserverWmtsLayer spreads it straight into GeoserverLayerOptions -- see TabPoisLocation,
          // which passes the same shape.
          LayerFactory.createGeoserverWmtsLayer(LAYER_BASEMAP, undefined, undefined, "basemap", undefined, {
              params: {
                  attributions: `© GeoBasis-DE / <a target="_blank" href="https://www.bkg.bund.de">BKG</a> (${basemapYear}) <a target="_blank" href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a>`,
              },
              title: "Basemap",
              type: "base",
              ...basemapCapabilities,
          } as any)
        : osmCapabilities
          ? LayerFactory.createGeoserverWmtsLayer(LAYER_POIS_DECORATED, addressData, undefined, "osm", undefined, {
                params: {
                    attributions:
                        '<a target="_blank" href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a>',
                },
                title: "OpenStreetMap",
                type: "base",
                ...osmCapabilities,
            } as any)
          : null;

    const shownFeatures = rankPoisWithSupermarketMinimum(
        features.filter((feature) => typeEnList.includes(feature.properties.type_en ?? "")),
        vehicle,
        POIS_PER_TYPE_IN_DESCRIPTION,
    );
    const isWideView = keywordName === "idv";
    const mapModel = {
        view: createScaleView(address, isWideView ? DEFAULT_TRAFFIC_SCALE : MINI_MAP_NEARBY_SCALE, null, {
            fixedZoom: true,
        }),
        layers: [
            ...(backgroundLayer ? [backgroundLayer] : []),
            LayerFactory.createCircleLayer(address, isWideView ? DEFAULT_TRAFFIC_RADII : DEFAULT_NEARBY_RADII),
            LayerFactory.createPoisLayer(vehicle, {type: "FeatureCollection", features: shownFeatures}, [], "pois", 0),
            LayerFactory.createMarkerLayer(address, language, "marker"),
        ],
    };

    return (
        <div className="OpenLayersMapBackdrop relative h-56 mb-3 rounded-border overflow-hidden">
            <MapLayerUnavailableHint layers={[LAYER_BASEMAP]} />
            <OpenLayersMap model={mapModel} />
        </div>
    );
}

/** Aktive Lärmquellen (inkl. Belastung in Umgebung) als Begründung für die Lärm-Einstufung. */
function NoiseTooltipTable() {
    const {t} = useTranslation();
    const noiseData = useSelector(selectNoiseData) as {noise: NoiseItem[]} | null;

    const rows = (noiseData?.noise ?? [])
        .filter((item) => item.dB !== NOISE_DB_NO_IMPACT)
        .sort((a, b) => (b.dbLow ?? -1) - (a.dbLow ?? -1))
        .slice(0, 5);

    if (rows.length === 0) {
        return <p className="m-0 text-sm">{t("market.description.tooltip.noNoise")}</p>;
    }

    return (
        <table className="text-sm w-full">
            <tbody>
                {rows.map((row) => (
                    <tr key={row.kategorie}>
                        <td className="pr-3">{row.kategorie}</td>
                        <td className="whitespace-nowrap">{noiseLevelLabel(row.dB, t)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

interface EmissionItem {
    kategorie: string;
    hasImpact: boolean;
    distanceM: number | null;
    name?: string | null;
}

/** Emissionsquellen mit tatsächlicher Auswirkung, nächste zuerst, als Begründung für die Emissions-Einstufung. */
function EmissionTooltipTable() {
    const {t, language} = useRevaTranslation();
    const emissionData = useSelector(selectEmissionData) as {emission: EmissionItem[]} | null;

    const rows = (emissionData?.emission ?? [])
        .filter((item) => item.hasImpact)
        .sort((a, b) => (a.distanceM ?? Number.POSITIVE_INFINITY) - (b.distanceM ?? Number.POSITIVE_INFINITY))
        .slice(0, 5);

    if (rows.length === 0) {
        return <p className="m-0 text-sm">{t("market.description.tooltip.noEmission")}</p>;
    }

    return (
        <table className="text-sm w-full">
            <tbody>
                {rows.map((row) => (
                    <tr key={row.kategorie}>
                        <td className="pr-3">{row.name || row.kategorie}</td>
                        <td className="whitespace-nowrap">{formatDistanceM(row.distanceM, language)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function KeywordTooltipContent({keywordName, isTooltip}: {keywordName: string; isTooltip: boolean}) {
    const kind = tooltipKindForKeyword(keywordName);
    if (kind === "poi")
        return (
            <PoiTooltipTable
                typeEnList={DESCRIPTION_KEYWORD_POI_TYPES[keywordName]}
                keywordName={keywordName}
                isTooltip={isTooltip}
            />
        );
    if (kind === "noise") return <NoiseTooltipTable />;
    if (kind === "emission") return <EmissionTooltipTable />;
    return null;
}

interface ChangeCategoryDialogProps {
    visible: boolean;
    onHide: () => void;
    word: string;
    keywordName: string;
    categories: AvmSpecParamCategory[];
    currentCategoryNumber: number;
    onSelectCategory: (category: AvmSpecParamCategory) => void;
}

/** Ziel-Unterseite in Tab Lage für den Detail-Link im Änderungsdialog, `null` ohne passende
 * Unterseite (z.B. "zentralitaet"). */
function locationTabUrlForKeyword(keywordName: string): string | null {
    const kind = tooltipKindForKeyword(keywordName);
    if (kind === "poi") return "/location/points-of-interest";
    if (kind === "noise" || kind === "emission") return "/location/emission";
    return null;
}

// Auf der Infrastruktur-Seite selbst zeigt die Auswahl ÖPNV/IDV unter "Anbindung", die übrigen
// POI-Keywords unter "Versorgung" (siehe TabPoisLocation, showTrafficMap).
const SUPPLY_KEYWORD_NAMES = new Set(["geschaefte", "einrichtungen", "naherholung"]);

/** Dialog zum Ändern der Kategorie eines Keywords, mit Begründung (falls Tooltip-Datenquelle vorhanden). */
function ChangeCategoryDialog({
    visible,
    onHide,
    word,
    keywordName,
    categories,
    currentCategoryNumber,
    onSelectCategory,
}: ChangeCategoryDialogProps) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const hasTooltip = tooltipKindForKeyword(keywordName) !== "none";
    const tabUrl = locationTabUrlForKeyword(keywordName);

    const categoryOptions = categories.map((category) => ({
        label: category.shortNameDe,
        value: category.categoryNumber,
    }));
    const onCategoryChange = (categoryNumber: number | null) => {
        const category = categories.find((c) => c.categoryNumber === categoryNumber);
        if (category) onSelectCategory(category);
    };
    const goToTab = () => {
        onHide();
        if (!tabUrl) return;
        if (tabUrl === "/location/points-of-interest") {
            dispatch(setShowTrafficMap(!SUPPLY_KEYWORD_NAMES.has(keywordName)));
        }
        void navigate(tabUrl);
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={t("market.description.changeCategoryDialogTitle", {keyword: word}) as string}
            dismissableMask
            showCloseIcon={false}
            focusOnShow={false}
            footer={
                <div className={`flex items-center w-full ${tabUrl ? "justify-between" : "justify-end"}`}>
                    {tabUrl && <Button label={t("market.description.goToTabButton")} onClick={goToTab} />}
                    <Button label={t("general.cancel")} icon="pi pi-times" severity="danger" text onClick={onHide} />
                </div>
            }
            onClick={(e) => e.stopPropagation()}
        >
            {hasTooltip && (
                <div className="element-panel mb-4">
                    {tooltipKindForKeyword(keywordName) === "poi" && (
                        <PoiTooltipMiniMap
                            typeEnList={DESCRIPTION_KEYWORD_POI_TYPES[keywordName]}
                            keywordName={keywordName}
                        />
                    )}
                    <KeywordTooltipContent keywordName={keywordName} isTooltip={false} />
                </div>
            )}
            <SelectButton
                value={currentCategoryNumber}
                options={categoryOptions}
                onChange={(e) => onCategoryChange(e.value)}
                allowEmpty={false}
                className="w-full"
                pt={{button: {className: "p-button-sm"}}}
            />
        </Dialog>
    );
}

interface DescriptionKeywordMarkerProps {
    token: KeywordToken;
    keywordName: string;
    category: AvmSpecParamCategory;
    categories: AvmSpecParamCategory[];
    readOnly: boolean;
    changed: boolean;
    onSelectCategory: (category: AvmSpecParamCategory) => void;
}

/**
 * Eine markierte Textstelle: Hover zeigt (wo eine Datenquelle existiert) die Begründung, Klick
 * öffnet bei Keys mit mehr als einer Kategorie einen Dialog zum Ändern der Einstufung. `changed`
 * hinterlegt sie hellblau, wenn ihre Kategorie vom Default-Text abweicht (siehe addedRanges).
 */
function DescriptionKeywordMarker({
    token,
    keywordName,
    category,
    categories,
    readOnly,
    changed,
    onSelectCategory,
}: DescriptionKeywordMarkerProps) {
    const spanRef = useRef<HTMLSpanElement>(null);
    const [dialogVisible, setDialogVisible] = useState(false);

    const word = category.shortNameDe;
    const isEditable = !readOnly && categories.length > 1;
    const hasTooltip = tooltipKindForKeyword(keywordName) !== "none";

    const selectCategory = (category: AvmSpecParamCategory) => {
        onSelectCategory(category);
        setDialogVisible(false);
    };

    return (
        <span onClick={(e) => e.stopPropagation()}>
            <span
                ref={spanRef}
                className={`font-bold underline decoration-dotted ${changed ? "description-diff-added " : ""}${isEditable ? "cursor-pointer" : ""}`}
                onClick={isEditable ? () => setDialogVisible(true) : undefined}
            >
                {word}
            </span>
            {hasTooltip && (
                <Tooltip
                    target={spanRef as RefObject<HTMLElement>}
                    className="custom-tooltip"
                    onBeforeShow={({target}) => {
                        // .custom-tooltip max-width ist 40rem; passt rechts vom Keyword nicht mehr hin -> oben zeigen.
                        const fitsRight = window.innerWidth - target.getBoundingClientRect().right >= 640;
                        target.setAttribute("data-pr-position", fitsRight ? "right" : "top");
                    }}
                >
                    <KeywordTooltipContent keywordName={keywordName} isTooltip={true} />
                </Tooltip>
            )}
            {isEditable && (
                <ChangeCategoryDialog
                    visible={dialogVisible}
                    onHide={() => setDialogVisible(false)}
                    word={word}
                    keywordName={keywordName}
                    categories={categories}
                    currentCategoryNumber={token.categoryNumber}
                    onSelectCategory={selectCategory}
                />
            )}
        </span>
    );
}

interface DescriptionRichTextFieldProps {
    text: string;
    defaultText: string | null;
    readOnly: boolean;
    onTextChange: (newText: string) => void;
    descriptionKeywordCategories: DescriptionKeywordCategoriesMap;
    descriptionKeywordKeyNames: DescriptionKeywordKeyNames;
}

/**
 * #18991: `text` ist die DOC_PROPERTY mit `[key:category_number]`- Platzhaltern
 * statt ausgeschriebener Keywords (siehe tokenizeText). Für jeden Platzhalter wird
 * die aktuelle Kategorie-Bezeichnung angezeigt; Hover zeigt bei ÖPNV etc. die nächstgelegenen Verkehrs-
 * POIs als Begründung, Klick öffnet einen Dialog zum Ändern der Einstufung. Eine Änderung ersetzt nur
 * das Token im Text und speichert ihn direkt in der bestehenden DOC_PROPERTY. Der Wechsel in den freien
 * Textmodus (Klick aufs Panel) wird vom Elternteil (TabMarketDescription) gesteuert; Keyword-Klicks
 * stoppen die Propagation, damit sie den Wechsel nicht mit auslösen.
 *
 * Abschnitte, die gegenüber `defaultText` neu oder geändert sind (siehe addedRanges), werden
 * hellblau hinterlegt -- sowohl Reintext als auch Keyword-Marker mit geänderter Kategorie.
 */
export default function DescriptionRichTextField({
    text,
    defaultText,
    readOnly,
    onTextChange,
    descriptionKeywordCategories,
    descriptionKeywordKeyNames,
}: DescriptionRichTextFieldProps) {
    const selectCategoryFor = (token: KeywordToken, category: AvmSpecParamCategory) => {
        onTextChange(replaceKeywordToken(text, token, category.categoryNumber));
    };

    const diffRanges = defaultText != null ? addedRanges(defaultText, text) : [];
    const isChanged = (rawStart: number, rawEnd: number) =>
        diffRanges.some((range) => range.start < rawEnd && range.end > rawStart);

    // Reintext-Abschnitte werden in ein Span mit ihrer Roh-Offset-Spanne getaggt (siehe
    // resolveClickedRawOffset unten) -- damit lässt sich beim Wechsel in den Freitextmodus die
    // Klickposition auf eine Cursorposition im Rohtext (`text`, mit [key:category_number]-Tokens)
    // zurückrechnen, statt den Cursor immer ans Textende zu setzen. Innerhalb eines Reintext-
    // Abschnitts wird zusätzlich an den diffRanges-Grenzen gesplittet, damit sich die Diff-
    // Hervorhebung auf den geänderten Teil beschränkt.
    const segments: ReactNode[] = [];
    const pushTextRun = (rawStart: number, rawEnd: number, changed: boolean) => {
        segments.push(
            <span
                key={`text-${rawStart}`}
                data-raw-start={rawStart}
                data-raw-end={rawEnd}
                className={changed ? "description-diff-added" : undefined}
            >
                {text.slice(rawStart, rawEnd)}
            </span>,
        );
    };
    const pushPlainSegment = (rawStart: number, rawEnd: number) => {
        if (rawEnd <= rawStart) return;
        let pos = rawStart;
        for (const range of diffRanges) {
            if (range.end <= pos || range.start >= rawEnd) continue;
            const changedStart = Math.max(range.start, pos);
            const changedEnd = Math.min(range.end, rawEnd);
            if (changedStart > pos) pushTextRun(pos, changedStart, false);
            pushTextRun(changedStart, changedEnd, true);
            pos = changedEnd;
        }
        if (pos < rawEnd) pushTextRun(pos, rawEnd, false);
    };
    let cursor = 0;
    for (const token of parseKeywordTokens(text)) {
        const categories = descriptionKeywordCategories[token.key] ?? [];
        const category = categories.find((c) => c.categoryNumber === token.categoryNumber);
        const keywordName = descriptionKeywordKeyNames[token.key];
        if (!category || !keywordName) continue; // unbekanntes/manuell verändertes Token: als Teil des umgebenden Texts belassen

        pushPlainSegment(cursor, token.matchStart);
        segments.push(
            <DescriptionKeywordMarker
                key={token.matchStart}
                token={token}
                keywordName={keywordName}
                category={category}
                categories={categories}
                readOnly={readOnly}
                changed={isChanged(token.matchStart, token.matchEnd)}
                onSelectCategory={(selected) => selectCategoryFor(token, selected)}
            />,
        );
        cursor = token.matchEnd;
    }
    pushPlainSegment(cursor, text.length);

    return <div className="whitespace-pre-wrap p-3">{segments}</div>;
}

/**
 * Rohtext-Offset (im [key:category_number]-Format) an der Klickposition, anhand der
 * `data-raw-start`/`data-raw-end`-Attribute der Reintext-Abschnitte (siehe oben). `null`, wenn die
 * Position keinem Reintext-Abschnitt zuzuordnen ist (z.B. Klick auf ein Keyword-Token, dessen Klick
 * ohnehin nicht bis zum Panel durchpropagiert, oder auf reines Padding).
 */
export function resolveClickedRawOffset(clientX: number, clientY: number): number | null {
    const doc = document as Document & {
        caretPositionFromPoint?: (x: number, y: number) => {offsetNode: Node; offset: number} | null;
        caretRangeFromPoint?: (x: number, y: number) => Range | null;
    };
    const caretPosition = doc.caretPositionFromPoint?.(clientX, clientY);
    const range = caretPosition ? null : doc.caretRangeFromPoint?.(clientX, clientY);
    const offsetNode = caretPosition?.offsetNode ?? range?.startContainer;
    const offset = caretPosition?.offset ?? range?.startOffset;
    if (!offsetNode || offset == null) return null;

    const element = offsetNode.nodeType === Node.TEXT_NODE ? offsetNode.parentElement : (offsetNode as Element);
    const span = element?.closest<HTMLElement>("[data-raw-start]");
    if (!span?.dataset.rawStart) return null;
    return Number(span.dataset.rawStart) + offset;
}
